/**
 * Runtime tileset: an API tileset descriptor plus its decoded atlas image and
 * the single source-of-truth slicing function (_docs/tilemap/02 §4). The
 * palette grid, chunk renderer, and (later) exporter all slice through
 * `tileRectPx` so there is exactly one place that knows the atlas geometry —
 * the web mirror of the engine's `GetTileUVs` rule (engine 46.2).
 */

import type { Tileset } from '../model/types';

export interface TileRect {
    x: number;
    y: number;
    w: number;
    h: number;
}

export class TilesetImage {
    private constructor(
        public readonly meta: Tileset,
        public readonly image: ImageBitmap,
    ) {}

    /**
     * Load a tileset's atlas. The texture is a normal project asset served at
     * `/api/assets/{uuid}`; `createImageBitmap` decodes it off the main thread.
     */
    static async load(meta: Tileset): Promise<TilesetImage> {
        if (!meta.texture_asset_uuid) {
            throw new Error(`Tileset ${meta.name} has no texture asset`);
        }
        const res = await fetch(`/api/assets/${meta.texture_asset_uuid}`);
        if (!res.ok) throw new Error(`Failed to load tileset texture (${res.status})`);
        const blob = await res.blob();
        const image = await createImageBitmap(blob);
        return new TilesetImage(meta, image);
    }

    get columns(): number {
        return this.meta.columns ?? Math.max(1, Math.floor(this.image.width / this.meta.tile_width));
    }

    /**
     * Source rectangle in the atlas for a given atlas index (row-major).
     * `atlasIndex = tileId - 1` (tile id 0 is empty).
     */
    tileRectPx(atlasIndex: number): TileRect {
        const { tile_width: tw, tile_height: th, spacing, margin } = this.meta;
        const col = atlasIndex % this.columns;
        const row = Math.floor(atlasIndex / this.columns);
        return {
            x: margin + col * (tw + spacing),
            y: margin + row * (th + spacing),
            w: tw,
            h: th,
        };
    }
}
